import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import LogoPolderzicht from '../assets/logo_polderzicht_kantoorlaag.png';
import '../css/CvControle.css';

const API_URL = '';

const translations = {
  nl: {
    title: 'Controle radiatoren en cv-installatie',
    subtitle: 'Geef hier door wat u heeft gevonden',
    deadline: 'Wij ontvangen uw melding graag uiterlijk 15 augustus 2026.',
    greeting: 'Beste bewoner,',
    intro: 'De ketels van onze centrale verwarmingsinstallatie zijn vernieuwd. Naar verwachting worden de nieuwe ketels in <strong>september 2026</strong> in gebruik genomen. Op dit moment zijn de cv-leidingen nog leeg. Voordat de installatie opnieuw met water wordt gevuld, vragen wij u de radiatoren, radiatorkranen en zichtbare cv-leidingen in uw woning goed te controleren.',
    why: 'In de afgelopen jaren zijn tijdens het stookseizoen meerdere lekkages ontstaan door roestvorming of slijtage. Wanneer een verzwakte radiator pas na het vullen gaat lekken, kan dit waterschade en overlast veroorzaken.',
    checklistTitle: 'Waar let u op?',
    checklist: [
      'roestplekken of afbladderende verf, vooral aan de onderkant en zijkanten van radiatoren;',
      'bruine, groene of witte aanslag bij aansluitingen en koppelingen;',
      'oude vochtplekken, verkleuringen of sporen van eerdere lekkage;',
      'scheurtjes, beschadigingen, bobbels of vervormingen in de radiator;',
      'beschadigde of sterk verroeste radiatorkranen;',
      'loszittende, beschadigde of sterk verroeste zichtbare cv-leidingen en koppelingen.'
    ],
    hidden: 'Controleer ook de delen die minder goed zichtbaar zijn, bijvoorbeeld achter meubels, gordijnen of een radiatorombouw. Een droge doek of stuk keukenpapier helpt om oude vocht- of roestsporen zichtbaar te maken.',
    warning: 'Let op: omdat de installatie nu leeg is, betekent het ontbreken van vocht niet automatisch dat alles in orde is. Draai zelf geen koppelingen, ontluchtingsventielen of leidingen los.',
    costsTitle: 'Reparatie en kosten',
    costPipes: 'Cv-leidingen: reparatie valt onder de verantwoordelijkheid en kosten van de VvE.',
    costRadiators: 'Radiatoren en radiatorkranen: reparatie of vervanging valt onder de verantwoordelijkheid en kosten van de bewoner/eigenaar.',

    houseNumber: 'Huisnummer',
    houseNumberPlaceholder: 'Bijv. 209',
    name: 'Naam',
    namePlaceholder: 'Uw volledige naam',
    email: 'E-mailadres',
    emailPlaceholder: 'uw@email.nl',
    emailHint: 'Voor de bevestiging van uw melding',
    phone: 'Telefoonnummer',
    phonePlaceholder: '06 12345678',
    phoneHint: 'Zodat de loodgieter een afspraak met u kan maken',

    statusLabel: 'Wat heeft u gevonden?',
    statusOk: 'Geen problemen',
    statusOkHint: 'Ik heb alles gecontroleerd en niets bijzonders gezien',
    statusProblem: 'Probleem gevonden',
    statusProblemHint: 'Roest, beschadiging, aanslag of een lekspoor',
    statusDoubt: 'Ik twijfel',
    statusDoubtHint: 'Ik weet niet zeker of dit in orde is',

    partsLabel: 'Om welk onderdeel gaat het?',
    partsHint: 'Meerdere antwoorden mogelijk',
    partRadiator: 'Radiator',
    partValve: 'Radiatorkraan',
    partPipe: 'Cv-leiding',

    location: 'Waar in de woning?',
    locationPlaceholder: 'Bijv. woonkamer, radiator onder het raam',
    description: 'Wat ziet u precies?',
    descriptionPlaceholder: 'Bijv. roest aan de onderkant van de radiator en een bruine aanslag bij de kraan...',
    selfRepair: 'Ik laat dit zelf door een erkende installateur herstellen',

    submit: 'Melding versturen',
    submitting: 'Bezig met versturen...',
    alreadyFilled: (date) => `U heeft op ${date} al een melding gedaan. U kunt deze hieronder aanpassen.`,
    successTitle: 'Bedankt!',
    successMessage: 'Uw melding is opgeslagen. Als u een e-mailadres heeft ingevuld, ontvangt u een bevestiging.',
    editAnswer: 'Melding aanpassen',
    footer: 'Vragen? Mail',

    errorRequired: 'Vul huisnummer en naam in',
    errorStatus: 'Kies wat u heeft gevonden',
    errorDescription: 'Beschrijf kort wat u heeft gezien',
    errorGeneral: 'Er is een fout opgetreden. Probeer het opnieuw.'
  },
  en: {
    title: 'Check your radiators and heating system',
    subtitle: 'Report here what you found',
    deadline: 'Please submit your report no later than 15 August 2026.',
    greeting: 'Dear resident,',
    intro: 'The boilers of our central heating system have been replaced. The new boilers are expected to become operational in <strong>September 2026</strong>. At present, the central-heating pipes are still empty. Before the system is refilled with water, we ask you to carefully check the radiators, radiator valves and visible central-heating pipes in your home.',
    why: 'In recent years, several leaks have occurred during the heating season because of rust or deterioration. When a weakened radiator only starts leaking after the system has been refilled, this can cause water damage and considerable inconvenience.',
    checklistTitle: 'What to look for',
    checklist: [
      'rust or flaking paint, particularly along the bottom and sides of radiators;',
      'brown, green or white deposits around connections and fittings;',
      'old damp patches, discolouration or signs of previous leakage;',
      'cracks, damage, bulges or deformation of a radiator;',
      'damaged or heavily corroded radiator valves;',
      'loose, damaged or heavily corroded visible pipes and connections.'
    ],
    hidden: 'Please also inspect parts that are less visible, such as areas behind furniture, curtains or radiator covers. A dry cloth or piece of kitchen paper may help reveal old moisture or rust marks.',
    warning: 'Please note: because the system is currently empty, the absence of moisture does not necessarily mean that everything is in good condition. Do not loosen any fittings, bleed valves or pipes yourself.',
    costsTitle: 'Repairs and costs',
    costPipes: 'Central-heating pipes: repairs are the responsibility and at the expense of the VvE.',
    costRadiators: 'Radiators and radiator valves: repair or replacement is the responsibility and at the expense of the resident/owner.',

    houseNumber: 'House number',
    houseNumberPlaceholder: 'E.g. 209',
    name: 'Name',
    namePlaceholder: 'Your full name',
    email: 'Email address',
    emailPlaceholder: 'your@email.com',
    emailHint: 'For the confirmation of your report',
    phone: 'Phone number',
    phonePlaceholder: '06 12345678',
    phoneHint: 'So the plumber can make an appointment with you',

    statusLabel: 'What did you find?',
    statusOk: 'No problems',
    statusOkHint: 'I checked everything and saw nothing unusual',
    statusProblem: 'Problem found',
    statusProblemHint: 'Rust, damage, deposits or traces of a leak',
    statusDoubt: 'I am unsure',
    statusDoubtHint: 'I am not certain whether this is in good condition',

    partsLabel: 'Which component does it concern?',
    partsHint: 'Multiple answers possible',
    partRadiator: 'Radiator',
    partValve: 'Radiator valve',
    partPipe: 'Central-heating pipe',

    location: 'Where in your home?',
    locationPlaceholder: 'E.g. living room, radiator under the window',
    description: 'What exactly do you see?',
    descriptionPlaceholder: 'E.g. rust along the bottom of the radiator and brown deposits near the valve...',
    selfRepair: 'I will arrange the repair myself through a recognised installer',

    submit: 'Submit report',
    submitting: 'Submitting...',
    alreadyFilled: (date) => `You already submitted a report on ${date}. You can update it below.`,
    successTitle: 'Thank you!',
    successMessage: 'Your report has been saved. If you provided an email address, you will receive a confirmation.',
    editAnswer: 'Edit report',
    footer: 'Questions? Email',

    errorRequired: 'Please fill in house number and name',
    errorStatus: 'Please choose what you found',
    errorDescription: 'Please briefly describe what you saw',
    errorGeneral: 'Something went wrong. Please try again.'
  }
};

const STATUS_OPTIONS = [
  { value: 'geen-problemen', icon: '✅', labelKey: 'statusOk', hintKey: 'statusOkHint' },
  { value: 'probleem', icon: '⚠️', labelKey: 'statusProblem', hintKey: 'statusProblemHint' },
  { value: 'twijfel', icon: '❓', labelKey: 'statusDoubt', hintKey: 'statusDoubtHint' }
];

const ONDERDEEL_OPTIONS = [
  { value: 'radiator', labelKey: 'partRadiator' },
  { value: 'radiatorkraan', labelKey: 'partValve' },
  { value: 'cv-leiding', labelKey: 'partPipe' }
];

const useQueryParam = (key) => {
  const { search } = useLocation();
  return new URLSearchParams(search).get(key);
};

const CvControle = () => {
  const initialHuisnummer = useQueryParam('h') || '';

  const [lang, setLang] = useState(localStorage.getItem('language') || 'nl');
  const [form, setForm] = useState({
    huisnummer: initialHuisnummer,
    naam: '',
    email: '',
    telefoon: '',
    status: '',
    onderdelen: [],
    locatie: '',
    omschrijving: '',
    zelfHerstel: false
  });
  const [existing, setExisting] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);

  const t = translations[lang];
  const heeftProbleem = form.status === 'probleem' || form.status === 'twijfel';

  useEffect(() => {
    localStorage.setItem('language', lang);
  }, [lang]);

  const checkExisting = useCallback(async (huisnummer) => {
    if (!huisnummer || !String(huisnummer).trim()) {
      setExisting(null);
      return;
    }
    try {
      const res = await fetch(`${API_URL}/api/cv-controle/check/${encodeURIComponent(huisnummer.trim())}`);
      if (!res.ok) return;
      const data = await res.json();
      if (data && data._id) {
        setExisting(data);
        setForm(prev => ({
          ...prev,
          naam: prev.naam || data.naam || '',
          email: prev.email || data.email || '',
          telefoon: prev.telefoon || data.telefoon || '',
          status: prev.status || data.status || '',
          onderdelen: prev.onderdelen.length ? prev.onderdelen : (data.onderdelen || []),
          locatie: prev.locatie || data.locatie || '',
          omschrijving: prev.omschrijving || data.omschrijving || '',
          zelfHerstel: prev.zelfHerstel || Boolean(data.zelfHerstel)
        }));
      } else {
        setExisting(null);
      }
    } catch (err) {
      // stil falen; het formulier blijft gewoon bruikbaar
    }
  }, []);

  useEffect(() => {
    if (initialHuisnummer) {
      checkExisting(initialHuisnummer);
    }
  }, [initialHuisnummer, checkExisting]);

  const handleHuisnummerBlur = () => {
    if (form.huisnummer) {
      checkExisting(form.huisnummer);
    }
  };

  const toggleOnderdeel = (value) => {
    setForm(prev => ({
      ...prev,
      onderdelen: prev.onderdelen.includes(value)
        ? prev.onderdelen.filter(o => o !== value)
        : [...prev.onderdelen, value]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    if (!form.huisnummer.trim() || !form.naam.trim()) {
      setError(t.errorRequired);
      return;
    }
    if (!form.status) {
      setError(t.errorStatus);
      return;
    }
    if (heeftProbleem && !form.omschrijving.trim()) {
      setError(t.errorDescription);
      return;
    }

    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/cv-controle/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      if (!res.ok) {
        setError(data.message || t.errorGeneral);
        return;
      }
      setSuccess(true);
      setExisting(data.melding);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (err) {
      setError(t.errorGeneral);
    } finally {
      setSubmitting(false);
    }
  };

  const formatDate = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    return d.toLocaleDateString(lang === 'nl' ? 'nl-NL' : 'en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const statusLabel = (value) => {
    const opt = STATUS_OPTIONS.find(o => o.value === value);
    return opt ? t[opt.labelKey] : '';
  };

  return (
    <div className="cvcontrole-page">
      <div className="cvcontrole-container">
        <div className="lang-toggle">
          <button
            className={`lang-btn ${lang === 'nl' ? 'active' : ''}`}
            onClick={() => setLang('nl')}
            type="button"
          >NL</button>
          <button
            className={`lang-btn ${lang === 'en' ? 'active' : ''}`}
            onClick={() => setLang('en')}
            type="button"
          >EN</button>
        </div>

        <div className="cvcontrole-header">
          <img src={LogoPolderzicht} alt="VVE Polderzicht" className="cvcontrole-logo" />
          <h1>{t.title}</h1>
          <p className="cvcontrole-subtitle">{t.subtitle}</p>
        </div>

        {success ? (
          <div className="cvcontrole-success">
            <div className="success-icon">✅</div>
            <h2>{t.successTitle}</h2>
            <p>{t.successMessage}</p>
            {existing && (
              <div className="success-summary">
                <div><strong>{t.houseNumber}:</strong> {existing.huisnummer}</div>
                <div><strong>{t.statusLabel.replace(/[?:]/g, '')}:</strong> {statusLabel(existing.status)}</div>
                {existing.omschrijving && (
                  <div><strong>{t.description.replace(/[?:]/g, '')}:</strong> {existing.omschrijving}</div>
                )}
              </div>
            )}
            <button
              type="button"
              className="cvcontrole-submit secondary"
              onClick={() => setSuccess(false)}
            >
              {t.editAnswer}
            </button>
          </div>
        ) : (
          <>
            <div className="cvcontrole-deadline">{t.deadline}</div>

            <div className="cvcontrole-info">
              <p>{t.greeting}</p>
              <p dangerouslySetInnerHTML={{ __html: t.intro }} />
              <p>{t.why}</p>
            </div>

            <details className="cvcontrole-details">
              <summary>{t.checklistTitle}</summary>
              <ul>
                {t.checklist.map((item, i) => <li key={i}>{item}</li>)}
              </ul>
              <p>{t.hidden}</p>
              <p className="cvcontrole-warning">{t.warning}</p>
            </details>

            <details className="cvcontrole-details">
              <summary>{t.costsTitle}</summary>
              <ul>
                <li>{t.costPipes}</li>
                <li>{t.costRadiators}</li>
              </ul>
            </details>

            {existing && existing.submittedAt && (
              <div className="cvcontrole-alert info">
                {t.alreadyFilled(formatDate(existing.submittedAt))}
              </div>
            )}

            {error && (
              <div className="cvcontrole-alert error">{error}</div>
            )}

            <form onSubmit={handleSubmit} className="cvcontrole-form">
              <div className="form-group">
                <label htmlFor="huisnummer">{t.houseNumber} *</label>
                <input
                  type="text"
                  id="huisnummer"
                  value={form.huisnummer}
                  onChange={(e) => setForm({ ...form, huisnummer: e.target.value })}
                  onBlur={handleHuisnummerBlur}
                  placeholder={t.houseNumberPlaceholder}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="naam">{t.name} *</label>
                <input
                  type="text"
                  id="naam"
                  value={form.naam}
                  onChange={(e) => setForm({ ...form, naam: e.target.value })}
                  placeholder={t.namePlaceholder}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="email">{t.email}</label>
                <input
                  type="email"
                  id="email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  placeholder={t.emailPlaceholder}
                />
                <span className="form-hint">{t.emailHint}</span>
              </div>

              <div className="form-group">
                <label>{t.statusLabel} *</label>
                <div className="status-options">
                  {STATUS_OPTIONS.map(opt => (
                    <button
                      key={opt.value}
                      type="button"
                      className={`status-card status-${opt.value} ${form.status === opt.value ? 'selected' : ''}`}
                      onClick={() => setForm({ ...form, status: opt.value })}
                    >
                      <div className="status-icon">{opt.icon}</div>
                      <div className="status-label">{t[opt.labelKey]}</div>
                      <div className="status-hint">{t[opt.hintKey]}</div>
                    </button>
                  ))}
                </div>
              </div>

              {heeftProbleem && (
                <div className="cvcontrole-probleem">
                  <div className="form-group">
                    <label>{t.partsLabel}</label>
                    <span className="form-hint">{t.partsHint}</span>
                    <div className="checkbox-row">
                      {ONDERDEEL_OPTIONS.map(opt => (
                        <label key={opt.value} className="checkbox-pill">
                          <input
                            type="checkbox"
                            checked={form.onderdelen.includes(opt.value)}
                            onChange={() => toggleOnderdeel(opt.value)}
                          />
                          <span>{t[opt.labelKey]}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="locatie">{t.location}</label>
                    <input
                      type="text"
                      id="locatie"
                      value={form.locatie}
                      onChange={(e) => setForm({ ...form, locatie: e.target.value })}
                      placeholder={t.locationPlaceholder}
                      maxLength={500}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="omschrijving">{t.description} *</label>
                    <textarea
                      id="omschrijving"
                      value={form.omschrijving}
                      onChange={(e) => setForm({ ...form, omschrijving: e.target.value })}
                      placeholder={t.descriptionPlaceholder}
                      rows={4}
                      maxLength={2000}
                    />
                  </div>

                  <div className="form-group">
                    <label htmlFor="telefoon">{t.phone}</label>
                    <input
                      type="tel"
                      id="telefoon"
                      value={form.telefoon}
                      onChange={(e) => setForm({ ...form, telefoon: e.target.value })}
                      placeholder={t.phonePlaceholder}
                    />
                    <span className="form-hint">{t.phoneHint}</span>
                  </div>

                  <label className="checkbox-line">
                    <input
                      type="checkbox"
                      checked={form.zelfHerstel}
                      onChange={(e) => setForm({ ...form, zelfHerstel: e.target.checked })}
                    />
                    <span>{t.selfRepair}</span>
                  </label>
                </div>
              )}

              <button
                type="submit"
                className="cvcontrole-submit"
                disabled={submitting}
              >
                {submitting ? t.submitting : t.submit}
              </button>
            </form>
          </>
        )}

        <div className="cvcontrole-footer">
          <p>{t.footer} <a href="mailto:bestuurpolderzicht@gmail.com">bestuurpolderzicht@gmail.com</a></p>
        </div>
      </div>
    </div>
  );
};

export default CvControle;
